import {useEffect, useState} from 'react';
import {useWatch} from 'react-hook-form';
import {
  AssigneeType,
  AuthorType,
  Department,
  getAssigneeUid,
  NEW_APPROVAL_SETUP_CATEGORY,
  NEW_EQUIPMENT_ORDER_CATEGORY,
  TicketAssignee,
  TicketAuthor,
  TicketStatus,
  UserRoles,
} from '../../..';
import {getAgentById} from '../../../firebase/queries/agents';
import {useUserInfo} from '../../../hooks';
import {
  FIELD_NAME_ASSIGNEE,
  FIELD_NAME_ASSOCIATED_AGENTS,
  FIELD_NAME_AUTHOR,
  FIELD_NAME_CATEGORY,
  TicketStatusRules,
} from '../constants/constants';
import {useIsEditingClosed} from './useIsEditingClosed';
import {useTicketStatus} from './useTicketStatus';
import {useUserDepartment} from './useUserDepartment';

export function useIsEditingDisabled() {
  const {isEditingClosed, closedStatuses} = useIsEditingClosed();

  const {
    uid,
    isErpAdmin,
    isErpManager,
    isErpUser,
    isAgentAdmin,
    isAgentMember,
    isPortal,
    userGroupId,
    role,
  } = useUserInfo();

  const {department} = useUserDepartment();
  const {status} = useTicketStatus();

  const [subAgentUids, setSubAgentUids] = useState<string[]>([]);

  const assignee: TicketAssignee = useWatch({name: FIELD_NAME_ASSIGNEE});
  const author: TicketAuthor = useWatch({name: FIELD_NAME_AUTHOR});
  const associatedAgents: string[] = useWatch({name: FIELD_NAME_ASSOCIATED_AGENTS});
  const statusOptions: TicketStatus[] = status ? TicketStatusRules[status as TicketStatus] : [];
  const category = useWatch({name: FIELD_NAME_CATEGORY});

  const isUserAuthor = author && author.id === uid;

  const isUserAssignee = assignee && getAssigneeUid(assignee) === uid;

  const isErpUserAssignee = assignee?.erpUser && uid === assignee.erpUser.id;

  const isErpDepartmentAssignee =
    assignee?.type === AssigneeType.erpDepartment && assignee?.erpDepartment === department;

  const isErpAssignee = isErpUserAssignee || isErpDepartmentAssignee;

  const isAuditor = role === UserRoles.auditor;

  const isUserFinanceDept = department === Department.finance;
  const isApprovableByFinDeptOnly =
    category?.category?.name &&
    [NEW_APPROVAL_SETUP_CATEGORY, NEW_EQUIPMENT_ORDER_CATEGORY].includes(category?.category?.name);

  //const isResolvableBy =

  // Editing is disabled when:
  // 1. Ticket is in a closed status
  // 2. Ticket is not in Draft status AND:
  //    - User is an auditor, OR
  //    - User is not an ERP user and not an agent admin
  const isNotDraftStatus = status !== TicketStatus.Draft;
  const isNotAuthorizedUser = !isErpUser && !isAgentAdmin;
  const isEditingDisabled =
    isEditingClosed || (isNotDraftStatus && (isAuditor || isNotAuthorizedUser));

  const canUpdateViewableBy =
    !(isEditingClosed || !(status === TicketStatus.Draft || isErpAssignee || isErpUser)) ||
    (isAgentAdmin &&
      author?.type !== AuthorType.erpUser &&
      status &&
      !closedStatuses.includes(status)) ||
    (isAgentMember && author?.id === uid && status && !closedStatuses.includes(status)) ||
    (isAgentMember &&
      author?.id &&
      subAgentUids.includes(author.id) &&
      status &&
      !closedStatuses.includes(status));

  let canAgentUpdateStatusToResolved = false;
  if (
    isPortal &&
    assignee?.group?.id === userGroupId &&
    assignee?.type &&
    [AssigneeType.agent, AssigneeType.merchant].includes(assignee.type) &&
    statusOptions.includes(TicketStatus.Closed) &&
    // portal users can't resolve approval setup tickets
    !isApprovableByFinDeptOnly
  ) {
    if (isAgentAdmin) {
      //The Group Admin Agent can Resolve any tickets Assigned to themself, one of their Merchants or their Agents.
      canAgentUpdateStatusToResolved = true;
    } else if (isAgentMember && uid && (associatedAgents ?? []).includes(uid)) {
      //The Assigned Agent can Resolve the ticket if they feel no further action is needed
      canAgentUpdateStatusToResolved = true;
    }
  }

  const isEditingDisabledStatus =
    (!isUserAssignee &&
      !isErpAdmin &&
      !isErpManager &&
      !(
        isUserAuthor &&
        status &&
        [TicketStatus.Closed, TicketStatus.Unresolved].includes(status) &&
        !isPortal
      ) &&
      !canAgentUpdateStatusToResolved) ||
    // New Approval Setup can be resolved only by Finance Department
    (isApprovableByFinDeptOnly &&
      status &&
      status === TicketStatus.InProgress &&
      !isErpAdmin &&
      !isUserFinanceDept);

  const isStatusChangeLocked = assignee?.type === AssigneeType.erpDepartment;

  useEffect(() => {
    if (!isAgentMember || !uid) {
      return;
    }

    getAgentById(uid)
      .then(agentView => {
        setSubAgentUids(agentView?.subAgentUids || []);
      })
      .catch((err: unknown) => {
        console.error(`Couldn't get agent`, err);
      });
  }, [uid]);

  return {
    canUpdateViewableBy,
    isEditingDisabled,
    isEditingDisabledStatus,
    isStatusChangeLocked,
    canAgentUpdateStatusToResolved,
  } as const;
}
